import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { cerrarSesion } from '../store/slices/usuario.slice';
import { toast } from 'react-toastify';

// Duración de la sesión: 22 horas
const SESSION_DURATION_MS = 22 * 60 * 60 * 1000;
// Tiempo antes de expirar para mostrar advertencia: 1 hora antes
const WARNING_TIME_MS = 1 * 60 * 60 * 1000;
// Intervalo de verificación: cada 5 minutos
const CHECK_INTERVAL_MS = 5 * 60 * 1000;

export const useSessionTimeout = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const warningShownRef = useRef(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    const checkSession = () => {
      try {
        const userInfoStr = localStorage.getItem('userInfoES');
        if (!userInfoStr) {
          return;
        }

        const userInfo = JSON.parse(userInfoStr);
        let loginTimestamp = userInfo?.loginTimestamp;

        // Si no hay timestamp pero hay token, establecer timestamp actual
        if (!loginTimestamp && userInfo?.token) {
          loginTimestamp = Date.now();
          userInfo.loginTimestamp = loginTimestamp;
          localStorage.setItem('userInfoES', JSON.stringify(userInfo));
        }

        // Si no hay token, no hay nada que verificar
        if (!userInfo?.token) {
          return;
        }

        const now = Date.now();
        const elapsed = now - loginTimestamp;
        const timeRemaining = SESSION_DURATION_MS - elapsed;

        // Si la sesión ha expirado
        if (elapsed >= SESSION_DURATION_MS) {
          dispatch(cerrarSesion());
          navigate('/');
          toast.error('Tu sesión ha expirado después de 22 horas. Por favor, inicia sesión nuevamente.');
          return;
        }

        // Si queda menos de 1 hora y aún no se ha mostrado la advertencia
        if (timeRemaining <= WARNING_TIME_MS && !warningShownRef.current) {
          const minutesRemaining = Math.floor(timeRemaining / (60 * 1000));
          const hoursRemaining = Math.floor(minutesRemaining / 60);
          const mins = minutesRemaining % 60;
          
          let timeText = '';
          if (hoursRemaining > 0) {
            timeText = `${hoursRemaining} hora${hoursRemaining > 1 ? 's' : ''}`;
            if (mins > 0) {
              timeText += ` y ${mins} minuto${mins > 1 ? 's' : ''}`;
            }
          } else {
            timeText = `${minutesRemaining} minuto${minutesRemaining > 1 ? 's' : ''}`;
          }

          toast.warning(
            `Tu sesión expirará en ${timeText}. Por favor, guarda tu trabajo y cierra sesión para iniciar nuevamente.`,
            {
              autoClose: 10000, // 10 segundos
              position: 'top-center',
            }
          );
          warningShownRef.current = true;
        }
      } catch (error) {
        console.error('Error al verificar sesión:', error);
      }
    };

    // Verificar inmediatamente
    checkSession();

    // Configurar intervalo para verificar periódicamente
    intervalRef.current = setInterval(checkSession, CHECK_INTERVAL_MS);

    // Limpiar intervalo al desmontar
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [navigate, dispatch]);

  // Resetear la advertencia cuando cambia el usuario
  useEffect(() => {
    warningShownRef.current = false;
  }, []);
};

